import { createApp, defineComponent } from "vue";
import { getProductImage, getImageWithSize, decode } from "../../js/lib/util";

const AnimationLoop = function (hoverContainer: HTMLElement) {
  if (hoverContainer.classList.contains("is-mouseover")) {
    hoverContainer.classList.add("is-playing");
  }
};

/**
 * This component is used throughout the site,
 * in product carousels and on the PLP page.
 */
export default (el: HTMLElement) => {
  const componentDefinition = defineComponent({
    name: "product-card",
    props: ["product", "decode", "featured", "showButton", "modClass"],

    /**
     * Data can come via a JSON string that needs to be
     * decoded, or via an javascript object, passed by a
     * parent component or a Vuex store.
     */
    data() {
      let product;
      if (!!this.decode && this.product) {
        product = JSON.parse(decode(this.product));
      } else {
        product = this.product || {
          title: "",
          price: "",
          featuredImage: "",
          variants: [],
          optionNames: [],
          metafields: [],
        };
      }

      return {
        ...product,
        activeOption: "",
        hover: false,
        animated: false,
        fps: 24,
      };
    },

    mounted() {
      this.activeOption = this.firstOption;
    },

    computed: {
      firstOption() {
        return (this.options || [])[0];
      },
      optionName() {
        return (this.optionNames || [])[0];
      },
      options() {
        return this[this.optionName];
      },
      imageSrc() {
        return getProductImage(
          this.optionName,
          this.activeOption,
          this.images,
          false,
          this.featuredImage
        );
      },
      link() {
        return `/products/${this.handle}${this.activeVariantUrl}`;
      },
      activeVariantUrl() {
        const firstVariant = this.variants.find(({ title = "", id }: { title?: string; id: string | number }) => {
          return ~title.indexOf(this.activeOption);
        });
        return firstVariant ? `?variant=${firstVariant.id}` : "";
      },
      isAnimated() {
        if (this.animated) {
          return true;
        }

        return false;
      },
      featuredImageUrl() {
        let path = "";
        if (this.featuredImage) {
          path = getImageWithSize(this.featuredImage, 600);
        } else {
          path = this.imageSrc || "";
        }

        if (!path) {
          return "";
        }

        // make sure if it starts with files/ then it is prefixed with /cdn/shop/
        if (path.startsWith("files/")) {
          path = `/cdn/shop/${path}`;
        }

        return path;
      },
      cardClass() {
        return `product-card ${this.modClass || ""}`.trim();
      },
      hoverImageUrl() {
        // Swap to the 2nd image on hover, when the product has one.
        return (this.images || [])[1] || "";
      },
      addVariantId() {
        const variants = this.variants || [];
        const match = variants.find(({ title = "" }: { title?: string }) => {
          return ~String(title).indexOf(this.activeOption);
        });
        return (match || variants[0] || {}).id || "";
      },
    },

    template: `
    <div :class="cardClass">
      <div>
        <a class="product-card__featured bg-parchment" :href="link">
          <div
            :class="['product-card__featured__image', { 'animate animate--hover': isAnimated }]"
            v-if="isAnimated"
            @mouseover="mouseoverHandler"
            @mouseleave="mouseleaveHandler"
            @AnimationEnd="AnimationEndHandler"
            ref="hoverContainer">
            <div :class="['product-card__image-container', { 'product-card__image-container--animated': isAnimated }]" :data-fps="fps">
              <img
                class="product-card__image"
                :src="featuredImageUrl"
                :alt="title"
                :title="title"
                width="600"
                height="600"
              />
              <img
                v-if="hoverImageUrl"
                class="product-card__image product-card__image--hover"
                :src="hoverImageUrl"
                :alt="title"
                :title="title"
                width="600"
                height="600"
                loading="lazy"
              />
            </div>
          </div>
          <div
            v-else
            class="product-card__featured__image">
            <div class="product-card__image-container">
              <img
                class="product-card__image"
                :src="featuredImageUrl"
                :alt="title"
                :title="title"
                width="600"
                height="600"
              />
              <img
                v-if="hoverImageUrl"
                class="product-card__image product-card__image--hover"
                :src="hoverImageUrl"
                :alt="title"
                :title="title"
                width="600"
                height="600"
                loading="lazy"
              />
            </div>
          </div>

          <span
            v-for="tag in (productTags || [])"
            :key="tag"
            class="product-card__badge">{{ tag }}</span>
        </a>

        <div class="product-card__details">
          <div class="product-card__info">
            <div class="product-card__reviews">
              <div
                class="klaviyo-star-rating-widget"
                :data-id="id"
                :data-product-title="title"
                :data-product-type="type"></div>
            </div>

            <a class="product-card__heading" :href="link">
              <p class="product-card__title" v-html="title"></p>
              <span class="product-card__price" v-html="price"></span>
            </a>

            <div v-if="type || unitSize" class="product-card__meta">
              <span v-if="type" class="product-card__meta-item">{{ type }}</span>
              <span v-if="unitSize" class="product-card__meta-item">{{ unitSize }}</span>
            </div>

            <p v-if="subtitle" class="product-card__description">{{ subtitle }}</p>
          </div>

          <div v-if="showButton" class="product-card__actions">
            <a class="product-card__button" :href="link">
              View product
            </a>

            <a
              v-if="available"
              class="product-card__button product-card__button--primary add-to-cart"
              :href="link"
              :data-variant-id="addVariantId"
              :aria-label="'Add ' + title + ' to cart'">
              <span class="button__el">Add to cart</span>
            </a>
            <span
              v-else
              class="product-card__button product-card__button--disabled"
              aria-disabled="true">
              <span class="button__el">Out of stock</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  `,

    methods: {
      changeActiveOption(value: string) {
        this.activeOption = value;
      },
      mouseoverHandler() {
        this.$refs.hoverContainer.classList.add("is-playing");
        this.$refs.hoverContainer.classList.add("is-mouseover");
      },
      mouseleaveHandler() {
        this.$refs.hoverContainer.classList.remove("is-mouseover");
      },
      AnimationEndHandler() {
        this.$refs.hoverContainer.classList.remove("is-playing");
        setTimeout(() => {
          AnimationLoop(this.$refs.hoverContainer);
        }, 100);
      },
    },
  });

  const instanceApp = createApp(componentDefinition as any);
  return instanceApp.mount(el);
};
